import React, { useId } from 'react'
import { BiTimeFive } from 'react-icons/bi'
import {
  DATE_RANGE_OPTIONS,
  DAYS_OF_WEEK,
  INSTALL_SCHEDULE_OPTIONS,
  SCHEDULE_OPTIONS,
  TIMEZONE_OPTS,
  WEEKDAY_SELECT,
  dateRangeOptionsFor,
  inputClass,
  labelClass,
  todayYmd,
} from './taskScheduleConstants'

export type TaskScheduleFieldsProps = {
  scheduleType: string
  onScheduleTypeChange: (v: string) => void
  time: string
  onTimeChange: (v: string) => void
  timezone: string
  onTimezoneChange: (v: string) => void
  dayOfWeek: number
  onDayOfWeekChange: (v: number) => void
  dayOfMonth: number
  onDayOfMonthChange: (v: number) => void
  runDate?: string
  onRunDateChange?: (v: string) => void
  dateRangeStrategy?: string
  onDateRangeStrategyChange?: (v: string) => void
  allowOnce?: boolean
  showDateRange?: boolean
  previewLine?: string | null
  compact?: boolean
}

function chipClass(selected: boolean, compact: boolean): string {
  const size = compact ? 'tw-px-3 tw-py-1.5 tw-text-sm' : 'tw-px-3 tw-py-2 tw-text-sm tw-flex-1 tw-min-w-0 tw-justify-center'
  const tone = selected
    ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
    : 'tw-bg-white tw-text-slate-600 tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50'
  return `tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-font-medium tw-cursor-pointer tw-transition-colors ${size} ${tone}`
}

export default function TaskScheduleFields({
  scheduleType,
  onScheduleTypeChange,
  time,
  onTimeChange,
  timezone,
  onTimezoneChange,
  dayOfWeek,
  onDayOfWeekChange,
  dayOfMonth,
  onDayOfMonthChange,
  runDate,
  onRunDateChange,
  dateRangeStrategy,
  onDateRangeStrategyChange,
  allowOnce = true,
  showDateRange = true,
  previewLine,
  compact = false,
}: TaskScheduleFieldsProps) {
  const id = useId()
  const frequencyOptions = allowOnce ? SCHEDULE_OPTIONS : INSTALL_SCHEDULE_OPTIONS
  const dateOptions = dateRangeOptionsFor(scheduleType)
  const timezoneKnown = (TIMEZONE_OPTS as readonly string[]).includes(timezone)
  const showOnceDate = scheduleType === 'once'
  const showWeekday = scheduleType === 'weekly'
  const showMonthDay = scheduleType === 'monthly' || scheduleType === 'quarterly'
  const dateRangeValueKnown = dateOptions.some((opt) => opt.value === dateRangeStrategy)
  const extraDateRange =
    showDateRange && dateRangeStrategy && !dateRangeValueKnown
      ? Object.values(DATE_RANGE_OPTIONS)
          .flat()
          .find((opt) => opt.value === dateRangeStrategy) || {
          value: dateRangeStrategy,
          label: dateRangeStrategy.replace(/_/g, ' '),
        }
      : null

  return (
    <div className="tw-space-y-3">
      <div>
        <span className={labelClass} id={`${id}-frequency`}>
          Frequency
        </span>
        <div className="tw-flex tw-gap-2 tw-flex-wrap" role="group" aria-labelledby={`${id}-frequency`}>
          {frequencyOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onScheduleTypeChange(opt.value)}
              aria-pressed={scheduleType === opt.value}
              className={chipClass(scheduleType === opt.value, compact)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {showWeekday && (
        <div>
          {compact ? (
            <span className={labelClass}>Day</span>
          ) : (
            <label className={labelClass} htmlFor={`${id}-day-of-week`}>
              Day
            </label>
          )}
          {compact ? (
            <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
              {DAYS_OF_WEEK.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => onDayOfWeekChange(i)}
                  aria-pressed={dayOfWeek === i}
                  className={`tw-px-2.5 tw-py-1 tw-text-xs tw-rounded-md tw-border tw-font-medium tw-transition-colors ${
                    dayOfWeek === i
                      ? 'tw-bg-blue-50 tw-border-blue-300 tw-text-blue-700'
                      : 'tw-bg-white tw-border-slate-300 tw-text-slate-700 hover:tw-bg-slate-50'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          ) : (
            <select
              id={`${id}-day-of-week`}
              className={inputClass}
              value={dayOfWeek}
              onChange={(e) => onDayOfWeekChange(Number(e.target.value))}
            >
              {WEEKDAY_SELECT.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div
        className={
          compact
            ? 'tw-grid tw-grid-cols-2 tw-gap-3'
            : 'tw-grid tw-grid-cols-1 tw-gap-3 min-[420px]:tw-grid-cols-2'
        }
      >
        {showOnceDate && (
          <div>
            <label className={labelClass} htmlFor={`${id}-run-date`}>
              Run date
            </label>
            <input
              id={`${id}-run-date`}
              type="date"
              min={todayYmd()}
              className={inputClass}
              value={runDate || ''}
              onChange={(e) => onRunDateChange?.(e.target.value)}
            />
          </div>
        )}
        {showMonthDay && (
          <div>
            <label className={labelClass} htmlFor={`${id}-day-of-month`}>
              {scheduleType === 'quarterly' ? 'Day of quarter-start month' : 'Day of month'}
            </label>
            <select
              id={`${id}-day-of-month`}
              className={inputClass}
              value={dayOfMonth}
              onChange={(e) =>
                onDayOfMonthChange(Math.max(1, Math.min(28, Number(e.target.value) || 1)))
              }
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={labelClass} htmlFor={`${id}-time`}>
            {!compact && <BiTimeFive className="tw-inline tw-w-3.5 tw-h-3.5 tw-mr-1" aria-hidden />}
            Time
          </label>
          <input
            id={`${id}-time`}
            type="time"
            className={inputClass}
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor={`${id}-timezone`}>
            Time zone
          </label>
          <select
            id={`${id}-timezone`}
            className={inputClass}
            value={timezone}
            onChange={(e) => onTimezoneChange(e.target.value)}
          >
            {timezoneKnown ? null : <option value={timezone}>{timezone}</option>}
            {TIMEZONE_OPTS.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
        {showDateRange && (
          <div>
            <label className={labelClass} htmlFor={`${id}-date-range`}>
              Date range
            </label>
            <select
              id={`${id}-date-range`}
              className={inputClass}
              value={dateRangeStrategy || dateOptions[0]?.value || ''}
              onChange={(e) => onDateRangeStrategyChange?.(e.target.value)}
            >
              {extraDateRange ? (
                <option value={extraDateRange.value}>{extraDateRange.label}</option>
              ) : null}
              {dateOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {previewLine ? (
        <p className="tw-m-0 tw-rounded-lg tw-border tw-border-blue-100 tw-bg-blue-50/80 tw-px-3 tw-py-2 tw-text-xs tw-leading-relaxed tw-text-slate-600">
          {previewLine}
        </p>
      ) : null}
    </div>
  )
}
